import type {AppConfig} from '../initialize';
import type {
  EventName,
  EventType,
  HCaptchaInvisibleInboundPayload as InboundPayload,
  HCaptchaInvisibleOutboundPayload as OutboundPayload,
} from '../../shared/HCaptchaInvisibleTypes';
import {initialize} from '../initialize';
import {INPUT_TAGS, OUTPUT_TAGS} from '../../shared/HCaptchaInvisibleTypes';
import hcaptchaPromise from './HCaptchaPromise';
import type {SendFn} from '../setupMessages';

const absurd = (value: never) => {
  throw new Error(`Unexpected value: ${value}`);
};

const sanitize = (str: string) => {
  return String(str).replace(/\W/g, '_');
};

// HCaptcha will consume window globals, but just the variable names, not their values.
// Each execute supports passing arbitrary data and we'll transform it into variable names
// and set them on window (and also clearing previously set ones).
let globals: Array<string> = [];
const setGlobals = (data: Record<string, string>) => {
  // Clear the previous globals we
  globals.forEach((global: string) => {
    // @ts-expect-error TS7015
    delete window[global];
  });

  globals = Object.keys(data).map((key) => {
    return `stripe_${sanitize(key)}_${sanitize(data[key])}`.substring(0, 10000);
  });

  globals.forEach((key) => {
    // @ts-expect-error TS7015
    window[key] = null;
  });
};

// we dispatch the motion event that occurs on the merchant's page
// on the hcaptcha iframe, so the event is used when calculating the
// captcha score
const simulateEvent = (
  type: EventType,
  eventName: EventName,
  coordinates?: {x: number; y: number},
) => {
  let event;
  switch (type) {
    case 'mouse':
      if (coordinates) {
        event = new MouseEvent(eventName, {
          clientX: coordinates.x,
          clientY: coordinates.y,
        });
      }
      break;
    case 'keyboard':
      event = new KeyboardEvent(eventName);
      break;
    case 'touch':
      if (coordinates) {
        const touch = new Touch({
          identifier: Date.now(),
          clientX: coordinates.x,
          clientY: coordinates.y,
          // default to using iframe's body as the target
          target: document.body,
        });
        event = new TouchEvent(eventName, {touches: [touch]});
      }
      break;
    case 'pointer':
      if (coordinates) {
        event = new PointerEvent(eventName, {
          clientX: coordinates.x,
          clientY: coordinates.y,
        });
      }
      break;
    default:
      return;
  }
  if (event) {
    document.dispatchEvent(event);
  }
};

const errorMessage = (error: unknown) => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'unknown';
};

type HCaptchaInstance = {
  element: HTMLElement;
  id: string;
};
const instanceMap: Record<string, HCaptchaInstance> = {};

export const hCaptchaInvisible = async (
  root: HTMLElement,
  action: InboundPayload,
  sendMessage: SendFn<'HCaptchaInvisible'>,
): Promise<OutboundPayload> => {
  const actionTag = action.tag;
  const hcaptcha = await hcaptchaPromise();

  switch (actionTag) {
    case INPUT_TAGS.initialize:
      if (instanceMap[action.message.sitekey]) {
        return {tag: OUTPUT_TAGS.load};
      }

      const instanceElement = document.createElement('div');
      instanceElement.id = `hcaptcha-invisible-${action.message.sitekey}`;
      root.appendChild(instanceElement);

      const instanceId = hcaptcha.render(instanceElement, {
        sitekey: action.message.sitekey,
        size: 'invisible',
        'error-callback': (error: string) => {
          sendMessage({
            tag: OUTPUT_TAGS.error,
            value: {
              error: errorMessage(error),
            },
          });
        },
      });

      instanceMap[action.message.sitekey] = {
        element: instanceElement,
        id: instanceId,
      };

      return {tag: OUTPUT_TAGS.load};

    case INPUT_TAGS.execute:
      setGlobals(action.message.data);
      const startTime = new Date().getTime();
      const {rqdata} = action.message;

      const instance = instanceMap[action.message.sitekey];

      let result;
      if (instance) {
        result = await hcaptcha.execute(instance.id, {
          async: true,
          ...(rqdata ? {rqdata} : null),
        });
      } else {
        result = await hcaptcha.execute({
          async: true,
          ...(rqdata ? {rqdata} : null),
        });
      }

      if (!result) {
        throw new Error('expected execute promise');
      }

      return {
        tag: OUTPUT_TAGS.response,
        value: {
          response: result.response,
          key: result.key || '',
          duration: new Date().getTime() - startTime,
        },
      };

    case INPUT_TAGS.sendFraudSignals:
      simulateEvent(
        action.message.type,
        action.message.eventName,
        action.message.coordinates,
      );
      return {
        tag: OUTPUT_TAGS.sendComplete,
      };

    default:
      return absurd(actionTag);
  }
};

const app = ({root, subscribe, send}: AppConfig<'HCaptchaInvisible'>) => {
  subscribe(async (messageData, respond) => {
    respond(await hCaptchaInvisible(root, messageData, send));
  });
};

initialize({
  app,
  owner: 'checkout',
});
